import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock } from "lucide-react";
import { BLOG_POSTS } from "../data/blogPosts";
import CTABand from "../components/CTABand";
import { useSEO } from "../lib/seo";

function renderContent(text) {
  // Light markdown-ish renderer for headings, lists, italics
  const lines = text.trim().split("\n");
  const blocks = [];
  let buf = [];
  let listBuf = [];
  let listType = null; // 'ol' or 'ul'

  const flushPara = () => {
    if (buf.length) {
      blocks.push({ type: "p", content: buf.join(" ") });
      buf = [];
    }
  };
  const flushList = () => {
    if (listBuf.length) {
      blocks.push({ type: listType, items: listBuf });
      listBuf = [];
      listType = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
      continue;
    }
    if (line.startsWith("## ")) {
      flushPara();
      flushList();
      blocks.push({ type: "h2", content: line.slice(3) });
    } else if (line.startsWith("### ")) {
      flushPara();
      flushList();
      blocks.push({ type: "h3", content: line.slice(4) });
    } else if (/^\d+\.\s/.test(line)) {
      flushPara();
      if (listType !== "ol") flushList();
      listType = "ol";
      listBuf.push(line.replace(/^\d+\.\s/, ""));
    } else if (line.startsWith("- ")) {
      flushPara();
      if (listType !== "ul") flushList();
      listType = "ul";
      listBuf.push(line.slice(2));
    } else if (line.startsWith("*") && line.endsWith("*") && line.length > 2) {
      flushPara();
      flushList();
      blocks.push({ type: "italic", content: line.replace(/^\*|\*$/g, "") });
    } else {
      flushList();
      buf.push(line);
    }
  }
  flushPara();
  flushList();

  const renderInline = (text) =>
    text
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-navy font-semibold">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em class="text-teal italic">$1</em>');

  return blocks.map((b, i) => {
    if (b.type === "h2")
      return (
        <h2
          key={i}
          className="font-serif text-3xl sm:text-4xl text-navy mt-14 mb-5 leading-tight"
          dangerouslySetInnerHTML={{ __html: renderInline(b.content) }}
        />
      );
    if (b.type === "h3")
      return (
        <h3
          key={i}
          className="font-serif text-2xl text-navy mt-10 mb-4"
          dangerouslySetInnerHTML={{ __html: renderInline(b.content) }}
        />
      );
    if (b.type === "italic")
      return (
        <p
          key={i}
          className="font-serif italic text-navy/65 text-base border-l-2 border-gold pl-6 my-8"
          dangerouslySetInnerHTML={{ __html: renderInline(b.content) }}
        />
      );
    if (b.type === "ol")
      return (
        <ol key={i} className="list-decimal pl-6 space-y-3 my-6 font-sans text-navy/80 leading-relaxed marker:text-gold marker:font-serif">
          {b.items.map((it, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: renderInline(it) }} />
          ))}
        </ol>
      );
    if (b.type === "ul")
      return (
        <ul key={i} className="space-y-3 my-6 font-sans text-navy/80 leading-relaxed">
          {b.items.map((it, j) => (
            <li key={j} className="flex gap-3">
              <span className="mt-2.5 w-1.5 h-1.5 bg-gold rotate-45 flex-shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: renderInline(it) }} />
            </li>
          ))}
        </ul>
      );
    return (
      <p
        key={i}
        className="font-sans text-base sm:text-lg text-navy/80 leading-relaxed my-5"
        dangerouslySetInnerHTML={{ __html: renderInline(b.content) }}
      />
    );
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useSEO(
    post
      ? {
          title: post.title,
          description: post.excerpt,
          path: `/knowledge-center/${post.slug}`,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.excerpt,
            "datePublished": post.date,
            "author": {
              "@type": "Person",
              "name": "Sierra Thomas",
              "url": "https://athenacrestdefense.com/about",
            },
            "publisher": {
              "@type": "Organization",
              "name": "AthenaCrest Defense",
              "logo": {
                "@type": "ImageObject",
                "url": "https://athenacrestdefense.com/images/logo.png",
              },
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://athenacrestdefense.com/knowledge-center/${post.slug}`,
            },
            "keywords": (post.keywords || []).join(", "),
            "articleSection": post.category,
          },
        }
      : {}
  );

  if (!post) return <Navigate to="/knowledge-center" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div data-testid="blog-post-page" className="bg-ivory">
      <article className="section-pad pt-32">
        <div className="container-luxe max-w-4xl">
          <Link
            to="/knowledge-center"
            className="inline-flex items-center gap-2 text-sm font-sans uppercase tracking-wide text-navy/60 hover:text-gold transition-colors mb-12"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="h-4 w-4" /> Knowledge Center
          </Link>

          <div className="overline mb-5">{post.category}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1] mb-8">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm font-sans text-navy/55 pb-10 border-b border-champagne-200 mb-12">
            <span className="inline-flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gold" /> {post.date}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4 text-gold" /> {post.readTime}
            </span>
          </div>

          <div className="prose-luxe">{renderContent(post.content)}</div>
        </div>
      </article>

      {/* RELATED */}
      <section className="bg-paper section-pad">
        <div className="container-luxe">
          <div className="overline mb-5">Continue Reading</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-navy mb-12">
            More from the field.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/knowledge-center/${p.slug}`}
                className="luxe-card group flex flex-col"
              >
                <div className="overline text-[10px] mb-3">{p.category}</div>
                <h3 className="font-serif text-2xl text-navy mb-4 group-hover:text-teal transition-colors">
                  {p.title}
                </h3>
                <p className="font-sans text-sm text-navy/65 leading-relaxed flex-1">
                  {p.excerpt}
                </p>
                <span className="inline-flex items-center gap-2 mt-6 text-xs uppercase tracking-wide text-gold font-sans">
                  Read article <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        testId="blogpost-cta-band"
        eyebrow="Apply the Framework"
        headline="Bring AthenaCrest into the conversation."
        sub="Every article reflects a real engagement methodology. Book a confidential consultation to apply it inside your facility."
      />
    </div>
  );
}
